import { useCallback, useEffect, useState } from "react";
import { useAtomValue, useStore } from "jotai";
import { useCategoryApi } from "@/hooks/apiHooks/useCategoryApi";
import { categoriesAtom } from "@/store/categoryAtoms";

// Category is required, so while its list is unavailable Save is disabled —
// and once the read has failed this says why, next to the button, instead of
// failing silently.
export const SAVE_BLOCKED_BY_CATEGORIES =
    "Can't save yet: the category list didn't load. Use Retry above.";

// What an option list's field has to show: its options, a read in flight with
// nothing to offer yet, or a read that failed with nothing to offer.
export type LookupState = "ready" | "loading" | "failed";

// The last settled result of a list's read; null while one is in flight.
type ReadOutcome = "loaded" | "failed" | null;

// A list that is held is usable whatever its read is doing — another page may
// have loaded it, and it still offers its options through a later failure.
// Otherwise the field is only ready once a read has succeeded (with no rows).
const lookupState = (outcome: ReadOutcome, heldCount: number): LookupState => {
    if (heldCount > 0 || outcome === "loaded") {
        return "ready";
    }

    return outcome === "failed" ? "failed" : "loading";
};

// One list's read, issued on mount and again by retry. The outcome is LOCAL
// (fresh on every mount) and starts null, so the first render is already
// "in flight" — nothing waits on a flag an effect sets a render later. Retry
// goes back to null in the handler, so Save stays blocked from the click until
// the retry settles.
const useLookupRead = (read: () => Promise<unknown>) => {
    const [outcome, setOutcome] = useState<ReadOutcome>(null);

    const load = useCallback(() => {
        read().then(
            () => setOutcome("loaded"),
            () => setOutcome("failed"),
        );
    }, [read]);

    useEffect(() => {
        load();
    }, [load]);

    const retry = () => {
        setOutcome(null);
        load();
    };

    return { outcome, retry };
};

// The option list the product create and edit forms read on mount. A failed
// read must not look like an empty dropdown, and a read in flight must not
// look like a list that is ready to save against, so the list reports a
// LookupState.
export const useProductFormLookups = () => {
    const { getCategories } = useCategoryApi();
    const categories = useAtomValue(categoriesAtom);
    const store = useStore();

    // One failure, one alert. With nothing held the field shows the failure
    // inline, so the toast is skipped; with a list already held the field
    // stays "ready" and the toast is the only thing that reports the failure.
    // Read from the store when the read is issued, so the callback stays
    // stable and the mount effect does not re-run when the list arrives.
    const readCategories = useCallback(
        () =>
            getCategories({
                skipErrorNotification: store.get(categoriesAtom).length === 0,
            }),
        [getCategories, store],
    );

    const categoriesRead = useLookupRead(readCategories);

    return {
        categories,
        categoriesState: lookupState(categoriesRead.outcome, categories.length),
        retryCategories: categoriesRead.retry,
    };
};
